import { FC } from "react";
import MembershipHero from "./components/MembershipHero";
import MembershipPricingCard from "./components/MembershipPricingCard";
import MembershipListSection from "./components/MembershipListSection";
import MembershipJourney from "./components/MembershipJourney";
import { JourneyStep, Stat } from "./components/types";
import useIsMobile from "@/custom_hooks/isMobile";

export type { JourneyStep, Stat };

interface MembershipDetailedSectionProps {
  variant: "light" | "dark";
  label: string;
  title: string;
  stats: Stat[];
  heroImage: string;
  heroImageMobile?: string;
  cardImage: string;
  cardBackgroundVideoSrc?: string;
  cardTitle: string;
  price: string;
  idealForTitle: string;
  idealFor: string[];
  featuresTitle: string;
  features: string[];
  journeyTitle: string;
  journey: JourneyStep[];
  onCompare?: () => void;
  journeyMaxVisibleSteps?: number;
  onJourneyMore?: () => void;
  journeyMoreLabel?: string;
}

const MembershipDetailedSection: FC<MembershipDetailedSectionProps> = ({
  variant,
  label,
  title,
  stats,
  heroImage,
  heroImageMobile,
  cardImage,
  cardBackgroundVideoSrc,
  cardTitle,
  price,
  idealForTitle,
  idealFor,
  featuresTitle,
  features,
  journeyTitle,
  journey,
  onCompare,
  journeyMaxVisibleSteps,
  onJourneyMore,
  journeyMoreLabel,
}) => {
  const isMobile = useIsMobile();

  const isDark = variant === "dark";
  const textClass = isDark ? "text-white" : "text-gray-900";
  const bgClass =
    isDark && isMobile
      ? "bg-[#090b0a]"
      : isDark
        ? "bg-[#262626]"
        : "bg-[#e7e7e7]";

  const inner = (
    <div className="flex w-full flex-col gap-[20px]">
      <MembershipHero
        label={label}
        title={title}
        stats={stats}
        heroImage={heroImage}
        heroImageMobile={heroImageMobile}
        isDark={isDark}
        bgClass={bgClass}
        textClass={textClass}
      />

      <div className="flex w-full flex-col items-start gap-[20px] px-4 md:px-0 lg:flex-row">
        <MembershipPricingCard
          cardImage={cardImage}
          backgroundVideoSrc={cardBackgroundVideoSrc}
          cardTitle={cardTitle}
          price={price}
          onCompare={onCompare}
          isDark={isDark}
          bgClass={bgClass}
          textClass={textClass}
        />

        <div className="flex w-full flex-1 flex-col gap-[20px]">
          <MembershipListSection
            title={idealForTitle}
            items={idealFor}
            bgClass={bgClass}
            textClass={textClass}
          />

          <MembershipListSection
            title={featuresTitle}
            items={features}
            bgClass={bgClass}
            textClass={textClass}
          />

          <MembershipJourney
            title={journeyTitle}
            journey={journey}
            isDark={isDark}
            bgClass={bgClass}
            textClass={textClass}
            maxVisibleSteps={journeyMaxVisibleSteps}
            onMoreClick={onJourneyMore}
            moreLabel={journeyMoreLabel}
          />
        </div>
      </div>
    </div>
  );

  if (isDark) {
    return (
      <div className="bg-dark w-full pb-10 md:pt-20 md:pb-20">
        <div className="mx-auto w-full max-w-[1220px]">{inner}</div>
      </div>
    );
  }

  return inner;
};

export default MembershipDetailedSection;
